// Pure view logic for Day Care sessions and orders: what the board offers next,
// how a session's state reads, and how a course reads. No React here, so the
// rules are tested on their own.

import { formatIstDate } from "../../../utils/istDateTime";
import { ADMISSION_STATUS, BED_AVAILABLE, END_MODE, SESSION_STATUS, WEEKDAY_LABELS } from "../constants";

/**
 * The board's next action for a session:
 *   release  first session of a patient -> admission desk (wizard)
 *   link     released; the desk admits -> tie the admission back
 *   checkin  repeat session -> one-click admission
 *   finish   admitted, machine not yet allocated -> finish the check-in
 *   chart    running
 *   none     done, missed, cancelled or converted
 */
export function nextAction(session) {
  const { status } = session || {};
  if (status === SESSION_STATUS.BOOKED) {
    if (!session.is_first) return "checkin";
    return session.ipd_queue_id ? "link" : "release";
  }
  if (status === SESSION_STATUS.ADMITTED) {
    if (session.admission_status === ADMISSION_STATUS.DISCHARGED) return "none";
    if (session.admission_status !== ADMISSION_STATUS.ALLOCATED) return "finish";
    return "chart";
  }
  return "none";
}

/** { label, tone } for the session's chip. Tones: good, warn, bad, info, neutral. */
export function statusChip(session) {
  const { status, admission_status: admissionStatus, flags = {} } = session || {};
  if (status === SESSION_STATUS.ADMITTED) {
    if (admissionStatus === ADMISSION_STATUS.DISCHARGED) return { label: "Done", tone: "good" };
    if (flags.must_convert) return { label: "Over 24 h - convert", tone: "bad" };
    if (flags.overrun) return { label: "Past shift end", tone: "warn" };
    return { label: admissionStatus === ADMISSION_STATUS.ALLOCATED ? "On machine" : "Admitted", tone: "info" };
  }
  if (status === SESSION_STATUS.CONVERTED) return { label: "Converted to inpatient", tone: "neutral" };
  if (status === SESSION_STATUS.DNA) return { label: "Did not attend", tone: "warn" };
  if (status === SESSION_STATUS.CANCELLED) return { label: "Cancelled", tone: "neutral" };
  if (session?.ipd_queue_id) return { label: "At admission desk", tone: "info" };
  return { label: "Booked", tone: "neutral" };
}

/** "Haemodialysis · Mon/Wed/Fri · 12 sessions" */
export function courseSummary(order) {
  const days = order?.repeat_days || [];
  if (!days.length) return `${order?.service_name} · single session`;
  const pattern = days.map((d) => WEEKDAY_LABELS[d]).join("/");
  const end = order.end_mode === END_MODE.DATE
    ? `until ${formatIstDate(order.end_date)}`
    : `${order.session_count} sessions`;
  return `${order.service_name} · ${pattern} · ${end}`;
}

const GROUP_LABELS = { CLEAN: "Clean", HBSAG: "HBsAg +", HCV: "HCV +", HIV: "HIV +" };

export function groupLabel(group) {
  return GROUP_LABELS[group] || group || "";
}

/** The board's headline numbers for a day. */
export function boardCounts(day) {
  const sessions = day?.sessions || [];
  const machines = (day?.unit?.machines || []).filter((m) => m.active);
  const running = (s) => s.status === SESSION_STATUS.ADMITTED && s.admission_status !== ADMISSION_STATUS.DISCHARGED;
  return {
    total: sessions.length,
    waiting: sessions.filter((s) => s.status === SESSION_STATUS.BOOKED).length,
    running: sessions.filter(running).length,
    done: sessions.filter((s) => s.admission_status === ADMISSION_STATUS.DISCHARGED).length,
    missed: sessions.filter((s) => s.status === SESSION_STATUS.DNA).length,
    machinesFree: machines.filter((m) => (day?.bed_status || {})[m.bed_code] === BED_AVAILABLE).length,
    machines: machines.length,
  };
}

/** "AM" for "Abhishek Mathur" — the session card's avatar. */
export function initialsOf(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  return parts.slice(0, 2).map((p) => p[0].toUpperCase()).join("");
}

// The IPD discharge pipeline's stages, in order (dischargeProcessApi advances
// ward -> billing -> nursing_discharge -> physical_discharge).
const DISCHARGE_STAGE_ORDER = ["ward", "billing", "nursing_discharge", "physical_discharge"];

/** The discharge as a step list, for the timeline the IPD Billing screens use. */
export function dischargeSteps(process, adviceFiled) {
  const reached = process ? DISCHARGE_STAGE_ORDER.indexOf(process.stage) : -1;
  const done = [
    Boolean(adviceFiled || process),
    Boolean(process),
    reached > 0,
    reached > 1,
    reached > 2,
  ];
  const labels = [
    "Discharge advice (doctor)",
    "Discharge initiated",
    "Summary approved, sent to billing",
    "Billing cleared",
    "Nurse checklist, physically discharged",
  ];
  const current = done.indexOf(false);
  return labels.map((label, i) => ({
    label,
    status: done[i] ? "done" : i === current ? "in-progress" : "pending",
  }));
}
